import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useArtifactEngine } from '@/hooks/useArtifactEngine';
import { useLandscapeArtifact } from '@/hooks/useLandscapeArtifact';

const ARTIFACT_PARAM = 'artifact';

export const useArtifactState = (
  activeChatId: string | null,
  isMobileDevice: boolean,
  agentStatus?: string,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const artifactOpen = searchParams.has(ARTIFACT_PARAM);

  const setArtifactOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setSearchParams((prev) => {
        const current = prev.has(ARTIFACT_PARAM);
        const next = typeof value === 'function' ? value(current) : value;
        if (next && !current) prev.set(ARTIFACT_PARAM, '');
        if (!next && current) prev.delete(ARTIFACT_PARAM);
        return prev;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const [artifactActivated, setArtifactActivated] = useState(false);
  const [artifactDesktopMaximized, setArtifactDesktopMaximized] = useState(false);
  const [mobileArtifactCardMinimized, setMobileArtifactCardMinimized] = useState(false);
  const [mobileArtifactOverlay, setMobileArtifactOverlay] = useState(false);

  const handleArtifactActivate = useCallback(() => {
    setArtifactActivated(true);
    setArtifactOpen(true);
  }, [setArtifactOpen]);

  const artifactEngine = useArtifactEngine(activeChatId, handleArtifactActivate, agentStatus);
  const hasArtifact = artifactActivated || artifactEngine.hasComponents || artifactEngine.hasPages;
  const { landscapeArtifact, setLandscapeArtifact } = useLandscapeArtifact(isMobileDevice, artifactActivated, hasArtifact);

  const prevChatIdRef = useRef(activeChatId);
  useEffect(() => {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = activeChatId;

    if (!prev) return;
    if (prev === activeChatId) return;

    setArtifactOpen(false);
    setArtifactActivated(false);
    setArtifactDesktopMaximized(false);
    setMobileArtifactCardMinimized(false);
    setMobileArtifactOverlay(false);
    setLandscapeArtifact(false);
  }, [activeChatId, setArtifactOpen, setLandscapeArtifact]);

  useEffect(() => {
    if (!artifactOpen) setArtifactDesktopMaximized(false);
  }, [artifactOpen]);

  const resetArtifacts = useCallback(() => {
    setArtifactOpen(false);
    setArtifactActivated(false);
    setArtifactDesktopMaximized(false);
    setMobileArtifactCardMinimized(false);
    setMobileArtifactOverlay(false);
    setLandscapeArtifact(false);
  }, [setArtifactOpen, setLandscapeArtifact]);

  return {
    artifactOpen,
    setArtifactOpen,
    artifactDesktopMaximized,
    setArtifactDesktopMaximized,
    hasArtifact,
    artifactEngine,
    landscapeArtifact,
    setLandscapeArtifact,
    mobileArtifactCardMinimized,
    setMobileArtifactCardMinimized,
    mobileArtifactOverlay,
    setMobileArtifactOverlay,
    resetArtifacts,
  };
};
